// Figuras de seguridad y salud en el trabajo (bloque 6, A y B).
//
// Fuente: DE 255 Art. 18 (tabla de las pp. 21-22, transcrita a mano el
// 23/10/2026 en AI MARKIT DOWNS\DE 255\DE255_Art18_tabla_transcrita.md) y
// AM 196 Art. 13 (horas mínimas, p. 16). Organismos paritarios: DE 255 Arts. 32,
// 33 y 34. Modalidad: DE 255 Arts. 19, 20 y 25 y AM 196 Arts. 10, 13 y 15.
//
// PENDIENTE F1 (sedes): el DE 255 cuenta por "lugar y/o centro de trabajo"
// (Arts. 18, 32 y 33; AM 196 Art. 21). Mientras no existan las sedes, todo se
// calcula con el total de trabajadores de la empresa. Además, AM 196 Art. 13:
// "Para las pequeñas empresas de bajo y mediano riesgo que cuenten con más de
// un centro de trabajo deberán designar un monitor de seguridad e higiene por
// cada lugar y/o centro de trabajo que supere los 9 trabajadores" (sin programar).

export type RiskLevel = 'bajo' | 'medio' | 'alto';
export type CompanySize = 'micro' | 'pequeña' | 'mediana_a' | 'mediana_b' | 'grande';
export type FiguraSST = 'monitor' | 'tecnico';
export type ModalidadSST = 'monitor' | 'tecnico' | 'servicio_externo';
export type ParitarioType = 'participacion_directa' | 'delegado' | 'comite';

export interface FilaFiguraSST {
  tamano: CompanySize;
  desde: number;
  hasta: number | null;          // null = en adelante
  riesgo: RiskLevel;
  figura: FiguraSST;
  porCentro: boolean;            // el texto dice "por lugar y/o centro de trabajo"
  adicionalCada: number | null;  // empresa grande: un técnico adicional por cada N trabajadores
  horasMes: number | null;       // AM 196 Art. 13; null = no está en el texto disponible
}

const R = (tamano: CompanySize, desde: number, hasta: number | null, riesgo: RiskLevel, figura: FiguraSST,
  porCentro: boolean, adicionalCada: number | null, horasMes: number | null): FilaFiguraSST =>
  ({ tamano, desde, hasta, riesgo, figura, porCentro, adicionalCada, horasMes });

/**
 * DE 255 Art. 18 fila por fila. Horas: AM 196 Art. 13; esa tabla, en la edición
 * transcrita, termina en Mediana "A" · Bajo: el resto queda "no confirmado" (null).
 * Pequeña · Medio: monitor (corrección de Kevin; antes salía técnico).
 */
export const TABLA_FIGURAS_SST: FilaFiguraSST[] = [
  R('micro',     1,   9,    'bajo',  'monitor', true,  null, 8),
  R('micro',     1,   9,    'medio', 'monitor', true,  null, 8),
  R('micro',     1,   9,    'alto',  'tecnico', false, null, 16),
  R('pequeña',   10,  49,   'bajo',  'monitor', true,  null, 16),
  R('pequeña',   10,  49,   'medio', 'monitor', false, null, 32),
  R('pequeña',   10,  49,   'alto',  'tecnico', false, null, 48),
  R('mediana_a', 50,  99,   'bajo',  'tecnico', false, null, 32),
  R('mediana_a', 50,  99,   'medio', 'tecnico', false, null, null),
  R('mediana_a', 50,  99,   'alto',  'tecnico', false, null, null),
  R('mediana_b', 100, 199,  'bajo',  'tecnico', false, null, null),
  R('mediana_b', 100, 199,  'medio', 'tecnico', false, null, null),
  R('mediana_b', 100, 199,  'alto',  'tecnico', false, null, null),
  R('grande',    200, null, 'bajo',  'tecnico', false, 200,  null),
  R('grande',    200, null, 'medio', 'tecnico', false, 200,  null),
  R('grande',    200, null, 'alto',  'tecnico', false, 100,  null),
];

export const CITA_FIGURA = 'DE 255 Art. 18';
export const CITA_HORAS = 'AM 196 Art. 13';
export const NOTA_TECNICOS_ADICIONALES = 'Interpretación por validar: 1 técnico + 1 por cada 200 trabajadores (cada 100 si el riesgo es alto).';

export function tamanoEmpresa(trabajadores: number): CompanySize {
  if (trabajadores <= 9) return 'micro';
  if (trabajadores <= 49) return 'pequeña';
  if (trabajadores <= 99) return 'mediana_a';
  if (trabajadores <= 199) return 'mediana_b';
  return 'grande';
}

export function filaFiguraSST(trabajadores: number, riesgo: RiskLevel): FilaFiguraSST {
  const tamano = tamanoEmpresa(trabajadores);
  return TABLA_FIGURAS_SST.find(f => f.tamano === tamano && f.riesgo === riesgo)!;
}

/** Figura que exige el Art. 18. */
export function figuraRequerida(trabajadores: number, riesgo: RiskLevel): FiguraSST {
  return filaFiguraSST(trabajadores, riesgo).figura;
}

/**
 * Técnicos que exige el Art. 18 (0 si exige monitor). Empresa grande: opción A
 * decidida por JC el 23/10/2026 (1 + ⌊trabajadores/200⌋, o /100 con riesgo
 * alto); solo se muestra en el Perfil, no cambia ningún "aplica".
 */
export function tecnicosRequeridos(trabajadores: number, riesgo: RiskLevel): number {
  const fila = filaFiguraSST(trabajadores, riesgo);
  if (fila.figura === 'monitor') return 0;
  return fila.adicionalCada ? 1 + Math.floor(trabajadores / fila.adicionalCada) : 1;
}

/** DE 255 Arts. 34, 33 y 32 (con el total de trabajadores hasta F1). */
export function paritarioRequerido(trabajadores: number): ParitarioType {
  if (trabajadores <= 9) return 'participacion_directa';
  if (trabajadores <= 49) return 'delegado';
  return 'comite';
}

/** Personal de salud del Art. 18 (columna "Personal de salud en el trabajo"). */
export function personalSaludRequerido(trabajadores: number): string[] {
  if (trabajadores <= 99) {
    return ['Un profesional médico con formación de 4to nivel en seguridad y salud en el trabajo de visita periódica'];
  }
  const p = ['Un profesional médico con formación de 4to nivel en seguridad y salud en el trabajo', 'Un profesional de enfermería'];
  if (trabajadores >= 300) p.push('Un profesional en psicología');
  if (trabajadores >= 1000) p.push('Un profesional médico con especialidad médica en Medicina del Trabajo');
  return p;
}

/** Modalidad por defecto: la figura que exige el Art. 18 (decisión de JC del 23/10/2026). */
export function modalidadPorDefecto(trabajadores: number, riesgo: RiskLevel): ModalidadSST {
  return figuraRequerida(trabajadores, riesgo);
}

export const MODALIDAD_LABELS: Record<ModalidadSST, string> = {
  monitor: 'Monitor propio',
  tecnico: 'Técnico propio',
  servicio_externo: 'Servicio externo',
};

export const MODALIDAD_CITAS: Record<ModalidadSST, string> = {
  monitor: 'DE 255 Arts. 18 y 19',
  tecnico: 'DE 255 Arts. 18 y 20',
  servicio_externo: 'DE 255 Art. 25; AM 196 Arts. 10 y 15',
};

export interface AplicaFiguras {
  ga04: boolean; ga05: boolean; ga06: boolean; ga07: boolean;
  /** Eligió "Monitor propio" y el Art. 18 exige técnico: GA-05 y GA-07 siguen aplicando. */
  avisoExigeTecnico: boolean;
}

/**
 * "Aplica" de GA-04 a GA-07 según la modalidad (decisión de JC del 23/10/2026):
 * monitor → GA-04; técnico → GA-05 y GA-07; servicio externo → GA-06 y GA-07.
 * Una empresa puede tener una figura superior a la requerida. Si elige monitor
 * y el Art. 18 exige técnico, GA-05 y GA-07 siguen aplicando.
 */
export function aplicaFiguras(modalidad: ModalidadSST, requerida: FiguraSST): AplicaFiguras {
  const exigeTecnico = modalidad === 'monitor' && requerida === 'tecnico';
  return {
    ga04: modalidad === 'monitor',
    ga05: modalidad === 'tecnico' || exigeTecnico,
    ga06: modalidad === 'servicio_externo',
    ga07: modalidad === 'tecnico' || modalidad === 'servicio_externo' || exigeTecnico,
    avisoExigeTecnico: exigeTecnico,
  };
}

/** ¿Se pueden calcular las figuras? Sin trabajadores o sin nivel de riesgo, no (nada de valores por defecto). */
export function perfilCompleto(p: { workers_count: number | null | undefined; risk_level: RiskLevel | null | undefined }): boolean {
  return typeof p.workers_count === 'number' && p.workers_count > 0 && !!p.risk_level;
}
